import { useState, useEffect, useCallback } from 'react';
import { Issue } from '../types';
import { ACTIVE_TIMER_STORAGE_KEY } from '../config';

export interface ActiveTimer {
  issueId: string;
  issueKey: string;
  issueSummary: string;
  jiraId: string;
  startTime: Date;
}

interface StoredActiveTimer {
  issueId: string;
  issueKey: string;
  issueSummary: string;
  jiraId: string;
  startTime: string;
}

const loadActiveTimerFromStorage = (): ActiveTimer | null => {
  try {
    const stored = localStorage.getItem(ACTIVE_TIMER_STORAGE_KEY);
    if (stored) {
      const parsed: StoredActiveTimer = JSON.parse(stored);
      return {
        ...parsed,
        startTime: new Date(parsed.startTime),
      };
    }
  } catch (error) {
    console.error('Failed to load active timer from storage:', error);
  }
  return null;
};

const saveActiveTimerToStorage = (timer: ActiveTimer | null): void => {
  try {
    if (timer) {
      const toStore: StoredActiveTimer = {
        ...timer,
        startTime: timer.startTime.toISOString(),
      };
      localStorage.setItem(ACTIVE_TIMER_STORAGE_KEY, JSON.stringify(toStore));
    } else {
      localStorage.removeItem(ACTIVE_TIMER_STORAGE_KEY);
    }
  } catch (error) {
    console.error('Failed to save active timer to storage:', error);
  }
};

interface UseActiveTimerReturn {
  activeTimer: ActiveTimer | null;
  elapsedTime: number;
  startTimer: (issue: Issue) => void;
  clearTimer: () => void;
}

/**
 * Hook that manages the active timer state with localStorage persistence.
 * Tracks elapsed time and provides functions to start/clear the timer.
 */
export const useActiveTimer = (): UseActiveTimerReturn => {
  const [activeTimer, setActiveTimer] = useState<ActiveTimer | null>(() => loadActiveTimerFromStorage());
  const [elapsedTime, setElapsedTime] = useState(0);

  // Persist activeTimer to localStorage
  useEffect(() => {
    saveActiveTimerToStorage(activeTimer);
  }, [activeTimer]);

  // Sync timer state across browser tabs
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === ACTIVE_TIMER_STORAGE_KEY) {
        const newTimer = loadActiveTimerFromStorage();
        setActiveTimer(newTimer);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Update elapsed time every second when timer is active
  useEffect(() => {
    if (!activeTimer) {
      setElapsedTime(0);
      return;
    }

    // Calculate elapsed time immediately, then update every second
    const calculateElapsed = () => {
      const start = activeTimer.startTime.getTime();
      const now = Date.now();
      return Math.floor((now - start) / 1000);
    };

    // Set initial value immediately
    setElapsedTime(calculateElapsed());

    const interval = setInterval(() => {
      setElapsedTime(calculateElapsed());
    }, 1000);

    return () => clearInterval(interval);
  }, [activeTimer]);

  const startTimer = useCallback((issue: Issue) => {
    setActiveTimer({
      issueId: issue.id,
      issueKey: issue.key,
      issueSummary: issue.summary,
      jiraId: issue.jiraId,
      startTime: new Date(),
    });
  }, []);

  const clearTimer = useCallback(() => {
    setActiveTimer(null);
  }, []);

  return {
    activeTimer,
    elapsedTime,
    startTimer,
    clearTimer,
  };
};
